import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useRealtime } from "@/hooks/useRealtime";
import {
  GripVertical, Send, Phone, Mail, Trash2, Plus, MessageSquare, ExternalLink,
  Clock, PlayCircle, CheckCircle2, PauseCircle, Flag, X, Users, Landmark, Copy,
  ChevronUp, ChevronDown, Flame, DollarSign, KeyRound, Paperclip, IdCard, RefreshCw, Edit3,
} from "lucide-react";
import ClientFilesModal from "@/components/app/ClientFilesModal";
import PersonalInfoModal from "@/components/app/PersonalInfoModal";

// Priority cycle: Low → Normal → High → Urgent → Low
const PRI_CYCLE = [20, 50, 70, 90];
const priBucket = (p) => {
  const v = p ?? 50;
  if (v >= 85) return { label: "Urgent", cls: "bg-rose-500 text-white ring-rose-300", dot: "bg-rose-500" };
  if (v >= 65) return { label: "High",   cls: "bg-amber-500 text-white ring-amber-300", dot: "bg-amber-500" };
  if (v >= 35) return { label: "Normal", cls: "bg-slate-200 text-slate-700 ring-slate-300", dot: "bg-slate-400" };
  return         { label: "Low",    cls: "bg-slate-100 text-slate-500 ring-slate-200", dot: "bg-slate-300" };
};
const cyclePriority = (p) => {
  const cur = p ?? 50;
  const idx = PRI_CYCLE.findIndex((x) => cur <= x);
  return PRI_CYCLE[(idx === -1 ? 0 : (idx + 1) % PRI_CYCLE.length)];
};

// Roles that can be assigned to handle a queue item
const HANDLER_ROLES = ["super_admin", "admin", "credit_specialist", "team_member"];
const staffLabel = (s) =>
  (s?.name || `${s?.first_name || ""} ${s?.last_name || ""}`.trim() || s?.email || "Staff");

const COLUMNS_FOR = (kind) => [
  { key: "queued",      title: "Queued",       tone: "amber",   icon: Clock },
  { key: "in_progress", title: "In Progress",  tone: "sky",     icon: PlayCircle },
  { key: "done",        title: kind === "tradeline" ? "Completed" : "Completed · Pending Payout",
                        tone: "emerald", icon: kind === "tradeline" ? CheckCircle2 : DollarSign },
];
const TONES = {
  amber:   "border-amber-200 bg-amber-50/50",
  sky:     "border-sky-200 bg-sky-50/50",
  emerald: "border-emerald-200 bg-emerald-50/50",
};
const HEADER_TONES = {
  amber:   "text-amber-700 bg-amber-100",
  sky:     "text-sky-700 bg-sky-100",
  emerald: "text-emerald-700 bg-emerald-100",
};

/**
 * Reusable Kanban board for both credit-report and tradeline queues.
 * Native HTML5 drag-and-drop, real-time SSE sync, per-card SMS template picker.
 *
 * Props:
 *   kind: 'credit_report' | 'tradeline'
 *   showAddForm: if true, render the "add client to queue" widget
 *   emptyHint: string shown when the queued column is empty
 *   onOpenClient?: (item) => void — optional deep-link handler
 */
export default function WorkQueueBoard({ kind, showAddForm = true, emptyHint, onOpenClient }) {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);      // for the add form
  const [staff, setStaff] = useState([]);          // assignable handlers
  const [drag, setDrag] = useState({ id: null, over: null });
  const [expanded, setExpanded] = useState({});    // id → bool
  const [filesTarget, setFilesTarget] = useState(null); // {id, name} for the Files modal
  const [infoTarget, setInfoTarget]   = useState(null); // {id, name, email} for the Personal Info modal
  const [syncedAt, setSyncedAt] = useState(null);       // last successful sync (ms)
  const [refreshing, setRefreshing] = useState(false);  // manual refresh in flight
  const [nowTs, setNowTs] = useState(Date.now());       // ticks so "updated Xs ago" stays fresh
  const [newTpl, setNewTpl] = useState(null);           // { key?, label, body } | null — composer (create/edit)
  const [savingTpl, setSavingTpl] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);  // template manager (edit/delete) modal
  const [customSend, setCustomSend] = useState(null);   // { item, body } | null — one-off typed message
  const [sendingCustom, setSendingCustom] = useState(false);

  const load = useCallback(async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      const [{ data: itemsRes }, { data: tplRes }] = await Promise.all([
        api.get(`/admin/work-queue?kind=${kind}`),
        api.get(`/admin/work-queue/sms-templates?kind=${kind}`),
      ]);
      setItems(itemsRes?.items || []);
      setTemplates(tplRes?.items || []);
      setSyncedAt(Date.now());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      if (manual) setRefreshing(false);
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  // Client search (only for credit_report — admins add clients directly)
  useEffect(() => {
    if (!showAddForm || kind !== "credit_report") return;
    api.get("/admin/clients").then(({ data }) => setClients(data || [])).catch(() => {});
  }, [showAddForm, kind]);

  // Assignable handlers (staff/admins) for the per-card "who's handling it" dropdown
  useEffect(() => {
    api.get("/admin/users")
      .then(({ data }) => setStaff((data || []).filter((u) => HANDLER_ROLES.includes(u.role) && u.is_active !== false)))
      .catch(() => {});
  }, []);

  // Real-time SSE sync — refresh on any work-queue event for this kind
  useRealtime((evt) => {
    if (!evt) return;
    if (evt.type === "work_queue" && evt.kind === kind) load();
  });

  // Polling safety-net + self-healing sync. A backgrounded tab used to go stale
  // because polling was skipped while hidden; now we ALSO refetch the instant the
  // tab regains focus/visibility or the network reconnects, so every admin's board
  // snaps back in sync the moment they look at it.
  const dragRef = useRef(false);
  useEffect(() => { dragRef.current = !!drag.id; }, [drag.id]);
  useEffect(() => {
    const t = setInterval(() => {
      if (dragRef.current) return;      // don't yank a card mid-drag
      load();
    }, 12000);
    return () => clearInterval(t);
  }, [load]);

  // Instant re-sync on focus / tab-visible / reconnect (covers missed SSE + stale snapshots)
  useEffect(() => {
    const refetch = () => { if (!dragRef.current) load(); };
    const onVis = () => { if (!document.hidden) refetch(); };
    window.addEventListener("focus", refetch);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", refetch);
    return () => {
      window.removeEventListener("focus", refetch);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", refetch);
    };
  }, [load]);

  // Tick so the "updated Xs ago" / out-of-sync indicator stays accurate
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);
  const secsAgo = syncedAt ? Math.round((nowTs - syncedAt) / 1000) : null;
  const isStale = secsAgo != null && secsAgo > 40;

  const byStatus = useMemo(() => {
    const g = { queued: [], in_progress: [], done: [] };
    for (const it of items) {
      if (it.status === "blocked") { g.in_progress.push(it); continue; } // legacy fold-in
      (g[it.status] || g.queued).push(it);
    }
    // Queued: priority DESC, then manual position ASC, then newest first
    g.queued.sort((a, b) => {
      const pa = a.priority ?? 50, pb = b.priority ?? 50;
      if (pb !== pa) return pb - pa;
      const posA = a.position ?? 9999, posB = b.position ?? 9999;
      if (posA !== posB) return posA - posB;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
    // In Progress / Done: chronological (newest first)
    for (const k of ["in_progress", "done"]) {
      g[k].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    }
    return g;
  }, [items]);

  // Drag handlers (native HTML5)
  const onDragStart = (e, id, fromStatus) => {
    setDrag({ id, over: null, from: fromStatus });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (drag.over !== colKey) setDrag((d) => ({ ...d, over: colKey }));
  };
  const onDrop = async (e, newStatus) => {
    e.preventDefault();
    const id = drag.id || e.dataTransfer.getData("text/plain");
    setDrag({ id: null, over: null, from: null });
    if (!id) return;
    const item = items.find((x) => x.id === id);
    if (!item || item.status === newStatus) return;
    // Optimistic update
    setItems((prev) => prev.map((x) => x.id === id ? { ...x, status: newStatus } : x));
    try {
      await api.patch(`/admin/work-queue/${id}`, { status: newStatus });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Move failed");
      load();
    }
  };

  // Card-onto-card drop = intra-column reorder
  const onDropOnCard = async (e, targetId, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const fromId = drag.id || e.dataTransfer.getData("text/plain");
    setDrag({ id: null, over: null, from: null });
    const target = items.find((x) => x.id === targetId);
    const source = items.find((x) => x.id === fromId);
    if (!source || !target) return;
    if (source.status !== target.status) {
      // cross-column drop resolves as a status move
      setItems((prev) => prev.map((x) => x.id === source.id ? { ...x, status: target.status } : x));
      try { await api.patch(`/admin/work-queue/${source.id}`, { status: target.status }); } catch {}
      return;
    }
    await reorderWithin(colKey, fromId, targetId);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove from queue?")) return;
    setItems((prev) => prev.filter((x) => x.id !== id));
    try { await api.delete(`/admin/work-queue/${id}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); load(); }
  };

  const payout = async (item) => {
    const amt = item.meta?.payout_amount;
    let label;
    if (kind === "tradeline") {
      label = `Archive ${item.client_name}? They'll be moved out of the active queue.`;
    } else {
      label = amt
        ? `Pay out $${Number(amt).toLocaleString()} to ${item.assigned_to_name || "worker"} for ${item.client_name}?`
        : `Mark ${item.client_name} as paid out and remove from board?`;
    }
    if (!window.confirm(label)) return;
    setItems((prev) => prev.filter((x) => x.id !== item.id));
    try {
      await api.post(`/admin/work-queue/${item.id}/payout`);
      toast.success(kind === "tradeline" ? `Archived · ${item.client_name}` : `Paid out · ${item.client_name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Payout failed");
      load();
    }
  };

  const sendSms = async (item, templateKey) => {
    if (!item.client_phone) { toast.error("No phone on file"); return; }
    try {
      const { data } = await api.post(`/admin/work-queue/${item.id}/send-sms`, { template_key: templateKey });
      toast.success(`Sent to ${item.client_name}: ${data.body?.slice(0, 40)}…`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "SMS failed");
    }
  };

  const bumpPriority = async (item, delta) => {
    const next = Math.max(0, Math.min(100, (item.priority ?? 50) + delta));
    setItems((p) => p.map((x) => x.id === item.id ? { ...x, priority: next } : x));
    try { await api.patch(`/admin/work-queue/${item.id}`, { priority: next }); } catch {}
  };

  const setPriority = async (item, value) => {
    const next = Math.max(0, Math.min(100, value));
    setItems((p) => p.map((x) => x.id === item.id ? { ...x, priority: next } : x));
    try { await api.patch(`/admin/work-queue/${item.id}`, { priority: next }); } catch {}
  };

  // Change a card's status straight from the card (no drag needed) — persists + syncs to everyone
  const setCardStatus = async (item, newStatus) => {
    if (!newStatus || item.status === newStatus) return;
    setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, status: newStatus } : x));
    try {
      await api.patch(`/admin/work-queue/${item.id}`, { status: newStatus });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Status update failed");
      load();
    }
  };

  // Create or edit a shared preset message for this board — visible to the whole team
  const saveNewTemplate = async () => {
    const label = (newTpl?.label || "").trim();
    const body = (newTpl?.body || "").trim();
    if (!label || !body) { toast.error("Give it a name and a message"); return; }
    setSavingTpl(true);
    try {
      if (newTpl.key) {
        await api.put(`/admin/work-queue/sms-templates/${newTpl.key}`, { kind, label, body });
        toast.success("Template updated for everyone");
      } else {
        await api.post("/admin/work-queue/sms-templates", { kind, label, body });
        toast.success("Template saved — everyone can use it now");
      }
      setNewTpl(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save template");
    } finally { setSavingTpl(false); }
  };

  const deleteTemplate = async (key) => {
    if (!window.confirm("Delete this template for the whole team?")) return;
    try {
      await api.delete(`/admin/work-queue/sms-templates/${key}`);
      toast.success("Template deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  // Send a one-off typed message straight to this client (not saved as a template)
  const sendCustom = async () => {
    const body = (customSend?.body || "").trim();
    if (!body) { toast.error("Type a message first"); return; }
    if (!customSend?.item?.client_phone) { toast.error("No phone on file"); return; }
    setSendingCustom(true);
    try {
      const { data } = await api.post(`/admin/work-queue/${customSend.item.id}/send-sms`, { template_key: "", override_body: body });
      toast.success(`Sent to ${customSend.item.client_name}: ${data.body?.slice(0, 40)}…`);
      setCustomSend(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "SMS failed");
    } finally { setSendingCustom(false); }
  };

  const setAssignee = async (item, id, name) => {    setItems((p) => p.map((x) => x.id === item.id ? { ...x, assigned_to_id: id, assigned_to_name: name } : x));
    try {
      await api.patch(`/admin/work-queue/${item.id}`, { assigned_to_id: id, assigned_to_name: name });
      toast.success(id ? `${item.client_name || "Item"} → ${name}` : `${item.client_name || "Item"} unassigned`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update handler");
      load();
    }
  };

  // Intra-column reorder — persists a manual "position" so priority ties keep the admin's order
  const reorderWithin = async (colKey, fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const col = (byStatus[colKey] || []).slice();
    const fromIdx = col.findIndex((x) => x.id === fromId);
    const toIdx = col.findIndex((x) => x.id === toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = col.splice(fromIdx, 1);
    col.splice(toIdx, 0, moved);
    // Assign fresh positions and, for the moved card, lift its priority so it beats ties
    const anchor = col[toIdx === 0 ? 1 : toIdx - 1];
    const anchorPri = anchor?.priority ?? 50;
    const liftedPri = Math.max(moved.priority ?? 50, anchorPri);
    setItems((prev) => prev.map((x) => x.id === moved.id ? { ...x, priority: liftedPri } : x));
    const payload = col.map((c, i) => ({ id: c.id, position: i + 1 }));
    try {
      await api.post("/admin/work-queue/reorder", { items: payload });
      await api.patch(`/admin/work-queue/${moved.id}`, { priority: liftedPri });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reorder failed");
      load();
    }
  };

  return (
    <div className="cq-in">
      {showAddForm && <AddToQueueForm kind={kind} clients={clients} onAdded={() => load()} />}

      {filesTarget && (
        <ClientFilesModal
          clientId={filesTarget.id}
          clientEmail={filesTarget.email}
          clientName={filesTarget.name}
          onClose={() => setFilesTarget(null)}
        />
      )}
      {infoTarget && (
        <PersonalInfoModal
          clientId={infoTarget.id}
          clientEmail={infoTarget.email}
          clientName={infoTarget.name}
          onOpenFiles={() => setFilesTarget(infoTarget)}
          onClose={() => setInfoTarget(null)}
        />
      )}

      {newTpl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="wq-tpl-modal"
          onMouseDown={() => setNewTpl(null)}
        >
          <Card className="w-full max-w-lg p-5 rounded-2xl" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-black text-slate-900">{newTpl.key ? "Edit preset message" : "New preset message"}</h3>
              <button onClick={() => setNewTpl(null)} className="text-slate-400 hover:text-slate-900" data-testid="wq-tpl-close">
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Saved to the whole team's {kind === "tradeline" ? "tradeline" : "credit"} board. Use{" "}
              <code className="text-amber-600">{"{first_name}"}</code> and{" "}
              <code className="text-amber-600">{"{agent_first_name}"}</code> to auto-fill names.
            </p>
            <div className="mt-3 space-y-2">
              <div>
                <Label className="text-xs">Template name</Label>
                <Input
                  value={newTpl.label}
                  onChange={(e) => setNewTpl({ ...newTpl, label: e.target.value })}
                  placeholder="e.g. 📄 Docs reminder"
                  data-testid="wq-tpl-label"
                />
              </div>
              <div>
                <Label className="text-xs">Message</Label>
                <Textarea
                  rows={4}
                  value={newTpl.body}
                  onChange={(e) => setNewTpl({ ...newTpl, body: e.target.value })}
                  placeholder="Hey {first_name}, quick update on your file…"
                  className="text-sm"
                  data-testid="wq-tpl-body"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setNewTpl(null)}>Cancel</Button>
              <Button
                onClick={saveNewTemplate}
                disabled={savingTpl}
                className="bg-amber-500 hover:bg-amber-600 text-white font-black"
                data-testid="wq-tpl-save"
              >
                {savingTpl ? "Saving…" : "Save for everyone"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {manageOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="wq-tpl-manager"
          onMouseDown={() => setManageOpen(false)}
        >
          <Card className="w-full max-w-lg p-5 rounded-2xl max-h-[85vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-black text-slate-900">Edit / delete templates</h3>
              <button onClick={() => setManageOpen(false)} className="text-slate-400 hover:text-slate-900" data-testid="wq-tpl-manager-close">
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">Built-in presets are locked. Your team's custom ones can be edited or removed.</p>
            <div className="mt-3 flex justify-end">
              <Button
                size="sm"
                onClick={() => { setManageOpen(false); setNewTpl({ label: "", body: "" }); }}
                className="bg-amber-500 hover:bg-amber-600 text-white font-black"
                data-testid="wq-tpl-manager-new"
              >
                <Plus size={13} className="mr-1" /> New template
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              {templates.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No templates yet.</p>}
              {templates.map((t) => (
                <div key={t.key} className="rounded-xl border border-slate-100 p-3" data-testid={`wq-tpl-row-${t.key}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-slate-900 truncate">{t.label}</p>
                        <span className={`text-[9px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 ${t.is_custom ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                          {t.is_custom ? "Custom" : "Built-in"}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1 whitespace-pre-wrap line-clamp-2">{t.body}</p>
                    </div>
                    {t.is_custom && (
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => { setManageOpen(false); setNewTpl({ key: t.key, label: t.label, body: t.body }); }}
                          className="text-slate-400 hover:text-sky-600 p-1"
                          title="Edit"
                          data-testid={`wq-tpl-edit-${t.key}`}
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => deleteTemplate(t.key)}
                          className="text-slate-400 hover:text-rose-600 p-1"
                          title="Delete"
                          data-testid={`wq-tpl-delete-${t.key}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {customSend && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="wq-custom-send"
          onMouseDown={() => setCustomSend(null)}
        >
          <Card className="w-full max-w-lg p-5 rounded-2xl" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-black text-slate-900">Text {customSend.item.client_name || "client"}</h3>
              <button onClick={() => setCustomSend(null)} className="text-slate-400 hover:text-slate-900" data-testid="wq-custom-close">
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              One-off message (not saved as a template). Sends to{" "}
              <span className="font-bold">{customSend.item.client_phone || "— no phone on file —"}</span>. Use{" "}
              <code className="text-amber-600">{"{first_name}"}</code> to auto-fill.
            </p>
            <Textarea
              rows={5}
              value={customSend.body}
              onChange={(e) => setCustomSend({ ...customSend, body: e.target.value })}
              placeholder={`Hey ${(customSend.item.client_name || "").split(" ")[0] || "there"}, quick note…`}
              className="mt-3 text-sm"
              data-testid="wq-custom-body"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setCustomSend(null)}>Cancel</Button>
              <Button
                onClick={sendCustom}
                disabled={sendingCustom || !customSend.item.client_phone}
                className="bg-slate-900 hover:bg-slate-800 text-white font-black"
                data-testid="wq-custom-send-btn"
              >
                <Send size={14} className="mr-1" /> {sendingCustom ? "Sending…" : "Send"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-[11px] font-bold">
          {isStale ? (
            <span className="inline-flex items-center gap-1 text-amber-600" data-testid="wq-sync-status">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> Out of sync — tap Refresh
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-emerald-600" data-testid="wq-sync-status">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
            </span>
          )}
          {secsAgo != null && (
            <span className="text-slate-400" data-testid="wq-sync-ago">
              · updated {secsAgo < 5 ? "just now" : `${secsAgo}s ago`}
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => load({ manual: true })}
          disabled={refreshing}
          className="h-8 text-xs font-bold border-slate-200"
          data-testid="wq-refresh"
        >
          <RefreshCw size={12} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {COLUMNS_FOR(kind).map((col) => {
          const list = byStatus[col.key] || [];
          const isOver = drag.over === col.key;
          const Icon = col.icon;
          const payoutTotal = (col.key === "done" && kind !== "tradeline")
            ? list.reduce((s, x) => s + (Number(x.meta?.payout_amount) || 0), 0)
            : 0;
          return (
            <div
              key={col.key}
              onDragOver={(e) => onDragOver(e, col.key)}
              onDrop={(e) => onDrop(e, col.key)}
              className={`rounded-2xl border-2 border-dashed transition ${TONES[col.tone]} ${isOver ? "ring-2 ring-slate-900 border-solid scale-[1.01]" : ""}`}
              data-testid={`wq-col-${col.key}`}
            >
              <div className={`px-3 py-2 rounded-t-xl inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-black ${HEADER_TONES[col.tone]}`}>
                <Icon size={12} /> {col.title}
                <span className="ml-1 h-5 min-w-5 px-1 rounded-full bg-white/60 text-[10px] inline-flex items-center justify-center">{list.length}</span>
                {payoutTotal > 0 && (
                  <span className="ml-1 rounded-full bg-emerald-600 text-white text-[10px] font-black px-2 py-0.5">
                    ${payoutTotal.toLocaleString()} owed
                  </span>
                )}
              </div>
              <div className="p-2 flex flex-col gap-2 min-h-[120px]">
                {list.length === 0 && col.key === "queued" && (
                  <p className="text-xs text-slate-400 text-center py-6">{emptyHint || "Drop items here"}</p>
                )}
                {list.map((it) => (
                  <WorkCard
                    key={it.id}
                    item={it}
                    kind={kind}
                    templates={templates}
                    dragging={drag.id === it.id}
                    onDragStart={(e) => onDragStart(e, it.id, col.key)}
                    onDragEnd={() => setDrag({ id: null, over: null, from: null })}
                    onDropOnCard={(e) => onDropOnCard(e, it.id, col.key)}
                    onSendSms={(tk) => sendSms(it, tk)}
                    onRemove={() => remove(it.id)}
                    onPayout={() => payout(it)}
                    onOpenClient={onOpenClient}
                    onOpenFiles={setFilesTarget}
                    onOpenInfo={setInfoTarget}
                    onBumpPriority={(d) => bumpPriority(it, d)}
                    onCyclePriority={() => setPriority(it, cyclePriority(it.priority))}
                    staff={staff}
                    onSetAssignee={(id, name) => setAssignee(it, id, name)}
                    onSetStatus={(s) => setCardStatus(it, s)}
                    onCreateTemplate={() => setNewTpl({ label: "", body: "" })}
                    onManageTemplates={() => setManageOpen(true)}
                    onCustomSend={() => setCustomSend({ item: it, body: "" })}
                    expanded={!!expanded[it.id]}
                    onToggle={() => setExpanded((s) => ({ ...s, [it.id]: !s[it.id] }))}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WorkCard({ item, kind, templates, dragging, onDragStart, onDragEnd, onDropOnCard, onSendSms, onRemove, onPayout, onOpenClient, onOpenFiles, onOpenInfo, onBumpPriority, onCyclePriority, staff, onSetAssignee, onSetStatus, onCreateTemplate, onManageTemplates, onCustomSend, expanded, onToggle }) {
  const [templateKey, setTemplateKey] = useState("");
  const days = item.created_at ? Math.floor((Date.now() - new Date(item.created_at).getTime()) / 86400000) : 0;
  const meta = item.meta || {};
  const pri = priBucket(item.priority);
  const isUrgent = (item.priority ?? 50) >= 85;
  const isDone = item.status === "done";
  const payoutAmt = Number(meta.payout_amount) || 0;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
      onDrop={onDropOnCard}
      className={`group rounded-xl border bg-white shadow-sm hover:shadow-md transition p-3 ${dragging ? "opacity-40" : ""} ${isUrgent ? "ring-1 ring-rose-300 border-rose-200" : ""}`}
      data-testid={`wq-card-${item.id}`}
    >
      <div className="flex items-start gap-2">
        <GripVertical size={14} className="text-slate-300 cursor-grab mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-black text-slate-900 truncate">{item.client_name || "Unnamed"}</p>
            <button
              onClick={onCyclePriority}
              title={`Priority ${item.priority ?? 50} — click to cycle`}
              className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 ring-1 hover:brightness-105 transition ${pri.cls}`}
              data-testid={`wq-pri-${item.id}`}
            >
              {isUrgent && <Flame size={10} />} {pri.label}
            </button>
            {days > 3 && <span className="text-[10px] font-bold text-rose-600">·{days}d</span>}
          </div>
          <p className="text-[11px] text-slate-500 truncate mt-0.5">
            {item.client_email}{item.client_phone ? ` · ${item.client_phone}` : ""}
          </p>
          {/* Who's handling it — anyone can pick / change */}
          <div
            className="mt-1.5 flex items-center gap-1.5"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          >
            <Users size={11} className="text-slate-400 shrink-0" />
            <select
              value={item.assigned_to_id || ""}
              draggable={false}
              onChange={(e) => {
                const id = e.target.value;
                const opt = (staff || []).find((s) => s.id === id);
                onSetAssignee(id, id ? staffLabel(opt) : "");
              }}
              className={`text-[11px] font-bold rounded-md border px-1.5 py-1 max-w-[160px] truncate cursor-pointer transition focus:outline-none focus:ring-2 focus:ring-sky-300 ${item.assigned_to_id ? "border-sky-300 bg-sky-50 text-sky-800" : "border-dashed border-slate-300 bg-slate-50 text-slate-500"}`}
              data-testid={`wq-assignee-${item.id}`}
              title="Who's handling this — pick or change"
            >
              <option value="">Unassigned</option>
              {(staff || []).map((s) => (
                <option key={s.id} value={s.id}>{staffLabel(s)}</option>
              ))}
              {item.assigned_to_id && !(staff || []).some((s) => s.id === item.assigned_to_id) && (
                <option value={item.assigned_to_id}>{item.assigned_to_name || "Assigned"}</option>
              )}
            </select>
          </div>
          {/* Update status right here — no drag needed. Hidden once Completed · Pending Payout (just awaiting your confirm). */}
          {!isDone && (
            <div
              className="mt-1.5 flex items-center gap-1.5"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              draggable={false}
            >
              <Flag size={11} className="text-slate-400 shrink-0" />
              <select
                value={item.status === "blocked" ? "in_progress" : item.status}
                draggable={false}
                onChange={(e) => onSetStatus(e.target.value)}
                className="text-[11px] font-bold rounded-md border border-slate-200 bg-white px-1.5 py-1 cursor-pointer transition focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid={`wq-status-${item.id}`}
                title="Move this client to another column"
              >
                <option value="queued">Queued</option>
                <option value="in_progress">In Progress</option>
                <option value="done">{kind === "tradeline" ? "Completed" : "Completed · Pending Payout"}</option>
              </select>
            </div>
          )}
          {kind === "tradeline" && meta.picks && (
            <p className="text-[11px] text-slate-500 mt-1 truncate">
              <Landmark size={10} className="inline mr-0.5" />
              {(meta.picks || []).map((p) => `${p.bank} $${(p.credit_limit || 0).toLocaleString()}`).join(" · ")}
            </p>
          )}
          {item.notes && <p className="text-[11px] text-slate-600 mt-1 italic">"{item.notes}"</p>}
        </div>
        <div className="flex flex-col items-center gap-0.5 shrink-0">
          <button
            onClick={() => onBumpPriority(10)}
            className="text-slate-300 hover:text-emerald-600 transition"
            title="Move up (priority +10)"
            data-testid={`wq-up-${item.id}`}
          >
            <ChevronUp size={14} />
          </button>
          <button
            onClick={() => onBumpPriority(-10)}
            className="text-slate-300 hover:text-slate-600 transition"
            title="Move down (priority -10)"
            data-testid={`wq-down-${item.id}`}
          >
            <ChevronDown size={14} />
          </button>
        </div>
        <button onClick={onRemove} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-500 transition self-start" title="Remove" data-testid={`wq-remove-${item.id}`}>
          <X size={14} />
        </button>
      </div>

      {/* Actions */}
      {isDone ? (
        <div className="mt-2 flex items-center gap-2">
          <Button
            size="sm"
            onClick={onPayout}
            className="flex-1 h-8 bg-emerald-600 hover:bg-emerald-700 text-white font-black"
            data-testid={`wq-payout-${item.id}`}
          >
            {kind === "tradeline"
              ? (<><CheckCircle2 size={12} className="mr-1" /> Archive</>)
              : (<><DollarSign size={12} className="mr-1" />
                    {payoutAmt > 0 ? `Pay Out $${payoutAmt.toLocaleString()} & Archive` : "Mark Paid & Archive"}
                 </>)}
          </Button>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-1 flex-wrap">
          <select
            value={templateKey}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__create__") { onCreateTemplate(); return; }
              if (v === "__manage__") { onManageTemplates(); return; }
              if (v === "__custom__") { onCustomSend(); return; }
              setTemplateKey(v);
            }}
            className="flex-1 min-w-[140px] h-8 rounded-md border border-slate-200 px-2 text-xs"
            data-testid={`wq-template-${item.id}`}
          >
            <option value="">Pick an SMS…</option>
            {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            <option value="__custom__">✍️ Type a custom message…</option>
            <option value="__create__">＋ Create new template…</option>
            <option value="__manage__">✏️ Edit / delete templates…</option>
          </select>
          <Button
            size="sm"
            onClick={() => templateKey && onSendSms(templateKey)}
            disabled={!templateKey || !item.client_phone}
            className="h-8 bg-slate-900 hover:bg-slate-800 text-white"
            data-testid={`wq-send-${item.id}`}
          >
            <Send size={12} className="mr-1" /> Send
          </Button>
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1 text-[11px] text-slate-500 flex-wrap">
        {item.close_lead_id ? (
          <a
            href={`https://app.close.com/lead/${item.close_lead_id}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-md px-1.5 py-1 transition"
            data-testid={`wq-open-${item.id}`}
            title="Open this lead directly in Close CRM"
          >
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_quack-dashboard-pro/artifacts/v0h3i5at_6467e2bcd35e329536073dcd_close_logo.png"
              alt=""
              className="h-3 w-3 object-contain"
              draggable={false}
            />
            Close
          </a>
        ) : (
          onOpenClient && (
            <button onClick={() => onOpenClient(item)} className="inline-flex items-center gap-1 font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-md px-1.5 py-1 transition" data-testid={`wq-open-${item.id}`}>
              <ExternalLink size={11} /> Open
            </button>
          )
        )}
        {(() => {
          const filesId = item.client_user_id || item.resolvable_id || item.client_email;
          if (!filesId) return null;
          return (
            <>
              <button
                onClick={() => onOpenFiles({ id: filesId, name: item.client_name, email: item.client_email })}
                className="inline-flex items-center gap-1 font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-md px-1.5 py-1 transition"
                data-testid={`wq-files-${item.id}`}
                title="View all files (Client + Jotform + Close CRM)"
              >
                <Paperclip size={11} /> Files
              </button>
              <button
                onClick={() => onOpenInfo({ id: filesId, name: item.client_name, email: item.client_email })}
                className="inline-flex items-center gap-1 font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-md px-1.5 py-1 transition"
                data-testid={`wq-info-${item.id}`}
                title="Personal info (name, SSN, DOB, address) pulled from Close + Jotform"
              >
                <IdCard size={11} /> Info
              </button>
            </>
          );
        })()}
        <button onClick={onToggle} className="ml-auto text-slate-400 hover:text-slate-900 px-1.5 py-1" data-testid={`wq-toggle-${item.id}`}>
          {expanded ? "Hide" : "More"}
        </button>
      </div>

      {/* Expanded credit-report detail — bureau login peek so Xzavier can jump in */}
      {expanded && kind === "credit_report" && item.client_user_id && (
        <BureauVaultPeek clientUserId={item.client_user_id} itemId={item.id} />
      )}
      {/* Expanded tradeline detail — sensitive AU info shown only when the card is expanded */}
      {expanded && kind === "tradeline" && (
        <div className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-700 grid grid-cols-2 gap-1">
          <div><b>SSN</b>: {maskSsn(meta.client_ssn) || <span className="text-slate-400">not provided</span>}</div>
          <div><b>DOB</b>: {meta.client_dob || <span className="text-slate-400">not provided</span>}</div>
          <div className="col-span-2"><b>Address</b>: {meta.client_address || <span className="text-slate-400">not provided</span>}</div>
          <div className="col-span-2"><b>Total</b>: ${meta.total_price} · {meta.payment_status}</div>
          {(meta.client_ssn || meta.client_dob) && (
            <button
              onClick={() => copyAll(item, meta)}
              className="col-span-2 mt-1 inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
              data-testid={`wq-copy-au-${item.id}`}
            >
              <Copy size={11} /> Copy AU info
            </button>
          )}
        </div>
      )}
      {expanded && item.sms_log && item.sms_log.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Recent SMS</p>
          {item.sms_log.slice(-3).reverse().map((l) => (
            <p key={l.id} className="text-[11px] text-slate-500 truncate">→ {l.body}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function maskSsn(s) {
  if (!s) return "";
  const digits = String(s).replace(/\D/g, "");
  if (digits.length < 4) return "•••-••-••••";
  return `•••-••-${digits.slice(-4)}`;
}

function copyAll(item, meta) {
  const lines = [
    `Client: ${item.client_name}`,
    `SSN: ${meta.client_ssn || ""}`,
    `DOB: ${meta.client_dob || ""}`,
    `Address: ${meta.client_address || ""}`,
    `Picks: ${(meta.picks || []).map((p) => `${p.bank} $${p.credit_limit}`).join(", ")}`,
  ].join("\n");
  navigator.clipboard.writeText(lines).then(() => toast.success("AU info copied"));
}

function AddToQueueForm({ kind, onAdded }) {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ name: "", email: "", phone: "", payout: "" });
  const [manualBusy, setManualBusy] = useState(false);
  const [destination, setDestination] = useState("queued"); // queued | done

  const load = useCallback(async (query = "") => {
    try {
      const url = query
        ? `/admin/work-queue/close-clients?q=${encodeURIComponent(query)}&limit=100`
        : `/admin/work-queue/close-clients?limit=100`;
      const { data } = await api.get(url);
      setContacts(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load Close contacts");
    }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q, open, load]);

  const addOne = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post("/admin/work-queue", {
        kind,
        client_user_id: c.user_id || undefined,
        client_name: c.name, client_email: c.email, client_phone: c.phone,
        initial_status: destination,
      });
      if (data?._requeued) {
        toast.success(`${c.name} was already on the board — moved back to Queued & priority bumped`);
      } else {
        toast.success(`${c.name} added to ${destination === "done" ? "Completed" : "queue"}`);
      }
      onAdded && onAdded();
      setContacts((prev) => prev.map((x) => x.id === c.id
        ? { ...x, on_queue: { ...(x.on_queue || {}), [kind]: true } }
        : x));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Add failed");
    } finally { setBusyId(null); }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manual.name.trim()) { toast.error("Name required"); return; }
    setManualBusy(true);
    try {
      const { data } = await api.post("/admin/work-queue", {
        kind,
        client_name: manual.name.trim(),
        client_email: manual.email.trim(),
        client_phone: manual.phone.trim(),
        initial_status: destination,
        payout_amount: manual.payout ? Number(manual.payout) : undefined,
      });
      if (data?._requeued) {
        toast.success(`${manual.name.trim()} was already on the board — moved back to Queued`);
      } else {
        toast.success(`${manual.name.trim()} added to ${destination === "done" ? "Completed" : "queue"}`);
      }
      setManual({ name: "", email: "", phone: "", payout: "" });
      setManualOpen(false);
      onAdded && onAdded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Add failed");
    } finally { setManualBusy(false); }
  };

  return (
    <Card className="p-3 rounded-2xl border-slate-100" data-testid="wq-add-form">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-slate-900"
        data-testid="wq-add-toggle"
      >
        <Plus size={14} /> {open ? "Close picker" : "Add someone to the queue"}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {/* Destination toggle — where does this add go? */}
          <div className="flex items-center gap-1 p-1 rounded-full bg-slate-100 w-fit">
            <button
              type="button"
              onClick={() => setDestination("queued")}
              className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest transition ${destination === "queued" ? "bg-amber-500 text-white shadow" : "text-slate-500 hover:text-slate-900"}`}
              data-testid="wq-dest-queued"
            >
              Queue it
            </button>
            <button
              type="button"
              onClick={() => setDestination("done")}
              className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest transition ${destination === "done" ? "bg-emerald-600 text-white shadow" : "text-slate-500 hover:text-slate-900"}`}
              data-testid="wq-dest-done"
            >
              Log as Completed
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Close contacts by name, email, or phone…"
              className="h-10 flex-1"
              data-testid="wq-search"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setManualOpen((v) => !v)}
              className="h-10 px-3 rounded-md border border-slate-200 hover:border-slate-400 text-xs font-bold text-slate-700 shrink-0"
              data-testid="wq-manual-toggle"
            >
              {manualOpen ? "Cancel manual" : "+ Manual add"}
            </button>
          </div>

          {manualOpen && (
            <form onSubmit={submitManual} className="grid grid-cols-1 sm:grid-cols-5 gap-2 p-2 rounded-lg bg-amber-50/60 border border-amber-100">
              <Input placeholder="Full name *" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} className="sm:col-span-1" data-testid="wq-manual-name" />
              <Input placeholder="Phone" value={manual.phone} onChange={(e) => setManual({ ...manual, phone: e.target.value })} className="sm:col-span-1" data-testid="wq-manual-phone" />
              <Input placeholder="Email" value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} className="sm:col-span-1" data-testid="wq-manual-email" />
              {destination === "done" ? (
                <Input placeholder="Payout $" type="number" min="0" value={manual.payout} onChange={(e) => setManual({ ...manual, payout: e.target.value })} className="sm:col-span-1" data-testid="wq-manual-payout" />
              ) : (
                <span className="hidden sm:block sm:col-span-1" />
              )}
              <Button type="submit" disabled={manualBusy} className={`h-10 rounded-md text-white ${destination === "done" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-500 hover:bg-amber-600"}`} data-testid="wq-manual-submit">
                {manualBusy ? "Adding…" : (destination === "done" ? "Completed" : "Add")}
              </Button>
            </form>
          )}

          <div className="max-h-72 overflow-y-auto flex flex-col gap-1 border border-slate-100 rounded-lg bg-white">
            {contacts.length === 0 && (
              <p className="p-4 text-xs text-slate-400 text-center">
                {q ? "No matches — try a different name, or use Manual add above." : "Loading Close contacts…"}
              </p>
            )}
            {contacts.map((c) => {
              const alreadyOn = !!c.on_queue?.[kind];
              return (
                <div
                  key={c.id || c.close_lead_id || c.email}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 transition"
                  data-testid={`wq-picker-${c.id || c.close_lead_id}`}
                >
                  <div className="h-7 w-7 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-xs font-black">
                    {(c.name || "?")[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">{c.name || "—"}</p>
                    <p className="text-[10px] text-slate-400 truncate">
                      {c._source === "close" ? "Close CRM" : "Local"} · {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                      {c.phone ? ` · ${c.phone}` : ""}
                    </p>
                  </div>
                  {alreadyOn ? (
                    <span className="text-[10px] uppercase tracking-widest font-black text-emerald-700 px-2">On queue</span>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => addOne(c)}
                      disabled={busyId === c.id}
                      className={`h-7 text-xs text-white ${destination === "done" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-500 hover:bg-amber-600"}`}
                      data-testid={`wq-add-${c.id || c.close_lead_id}`}
                    >
                      {destination === "done" ? "Completed" : "Add"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

/** Inline admin peek of the client's Credit Bureau Vault. Loaded on demand. */
function BureauVaultPeek({ clientUserId, itemId }) {
  const [state, setState] = useState({ loading: false, data: null, err: null });
  const [reveal, setReveal] = useState({});
  const [copied, setCopied] = useState("");

  const load = async () => {
    setState({ loading: true, data: null, err: null });
    try {
      const { data } = await api.get(`/admin/clients/${clientUserId}/credit-credentials`);
      setState({ loading: false, data, err: null });
    } catch (e) {
      setState({ loading: false, data: null, err: formatApiError(e.response?.data?.detail) || "Failed to load" });
    }
  };

  const copy = (key, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(""), 900);
    });
  };

  const PROVIDER_META = {
    experian:     { name: "Experian",     color: "text-[#1F3765]" },
    credit_karma: { name: "Credit Karma", color: "text-[#00A94F]" },
    identity_iq:  { name: "Identity IQ",  color: "text-[#F5A623]" },
  };

  return (
    <div className="mt-2 pt-2 border-t border-slate-100" data-testid={`wq-vault-peek-${itemId}`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Bureau logins</p>
        {!state.data && !state.loading && (
          <button
            onClick={load}
            className="text-[11px] font-bold text-slate-700 hover:text-amber-600 inline-flex items-center gap-1"
            data-testid={`wq-vault-load-${itemId}`}
          >
            <KeyRound size={11} /> Reveal
          </button>
        )}
        {state.loading && <span className="text-[11px] text-slate-400">Loading…</span>}
      </div>
      {state.err && <p className="mt-1 text-[11px] text-rose-600">{state.err}</p>}
      {state.data && (
        <div className="mt-1 space-y-1">
          {Object.entries(state.data.providers || {}).map(([key, p]) => {
            const meta = PROVIDER_META[key];
            if (!meta) return null;
            const has = p.username || p.has_password;
            return (
              <div key={key} className="flex items-center gap-2 text-[11px]">
                <span className={`font-black w-20 shrink-0 ${meta.color}`}>{meta.name}</span>
                {has ? (
                  <>
                    {p.username && (
                      <button
                        onClick={() => copy(`${key}-u`, p.username)}
                        className="truncate text-slate-700 hover:text-amber-600"
                        title="Copy username"
                      >
                        {p.username}
                      </button>
                    )}
                    {p.has_password && (
                      <>
                        <span className="text-slate-300">·</span>
                        <button
                          onClick={() => copy(`${key}-p`, p.password)}
                          className="text-slate-700 hover:text-amber-600 font-mono"
                          title="Copy password"
                          data-testid={`wq-vault-pw-${key}-${itemId}`}
                        >
                          {reveal[key] ? p.password : "•••••••"}
                        </button>
                        <button
                          onClick={() => setReveal((r) => ({ ...r, [key]: !r[key] }))}
                          className="text-slate-400 hover:text-slate-900"
                          title={reveal[key] ? "Hide" : "Reveal"}
                        >
                          {reveal[key] ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                      </>
                    )}
                    {copied.startsWith(key) && <span className="text-emerald-600 font-bold text-[10px]">copied</span>}
                    {p.notes && <span className="text-slate-400 italic truncate">· {p.notes}</span>}
                  </>
                ) : (
                  <span className="text-slate-400">not provided</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Tiny inline eye icons so we don't have to import more from lucide.
function EyeIcon()   { return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>; }
function EyeOffIcon(){ return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-10-7-10-7a19.7 19.7 0 0 1 4.22-5.19"/><path d="M1 1l22 22"/><path d="M9.5 9.5a3 3 0 0 0 4.24 4.24"/></svg>; }

